import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  adminService,
  SubscriptionCreate,
  SubscriptionUpdate,
  BusinessExpenseCreate,
  BusinessExpenseUpdate,
  TenantUpdate,
  TenantWithAdminCreate,
} from '@/services/admin';

// Dashboard
export function useAdminStats() {
  return useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: adminService.getStats,
    refetchInterval: 60000, // Refrescar cada minuto
  });
}

export function useAdminRevenueHistory(months: number = 6) {
  return useQuery({
    queryKey: ['admin', 'revenue-history', months],
    queryFn: () => adminService.getRevenueHistory(months),
  });
}

export function useAdminExpenseHistory(months: number = 6) {
  return useQuery({
    queryKey: ['admin', 'expense-history', months],
    queryFn: () => adminService.getExpenseHistory(months),
  });
}

export function useAdminProfitHistory(months: number = 6) {
  return useQuery({
    queryKey: ['admin', 'profit-history', months],
    queryFn: () => adminService.getProfitHistory(months),
  });
}

export function useAdminAlerts() {
  return useQuery({
    queryKey: ['admin', 'alerts'],
    queryFn: adminService.getAlerts,
    refetchInterval: 30000, // Refrescar cada 30 segundos
  });
}

// Tenants
export function useAdminTenants() {
  return useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: adminService.getTenants,
  });
}

export function useAdminTenant(id: number | null) {
  return useQuery({
    queryKey: ['admin', 'tenants', id],
    queryFn: () => adminService.getTenant(id!),
    enabled: !!id,
  });
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: TenantUpdate }) =>
      adminService.updateTenant(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

export function useCreateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TenantWithAdminCreate) => adminService.createTenant(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

export function useAdminTenantUsers(tenantId: number | null) {
  return useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'users'],
    queryFn: () => adminService.getTenantUsers(tenantId!),
    enabled: !!tenantId,
  });
}

// Subscriptions
export function useAdminSubscriptions() {
  return useQuery({
    queryKey: ['admin', 'subscriptions'],
    queryFn: adminService.getSubscriptions,
  });
}

export function useAdminSubscription(id: number | null) {
  return useQuery({
    queryKey: ['admin', 'subscriptions', id],
    queryFn: () => adminService.getSubscription(id!),
    enabled: !!id,
  });
}

export function useCreateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SubscriptionCreate) => adminService.createSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

export function useUpdateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SubscriptionUpdate }) =>
      adminService.updateSubscription(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

// Expenses
export function useAdminExpenses(filters?: {
  category?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: filters ? ['admin', 'expenses', filters] : ['admin', 'expenses'],
    queryFn: () => adminService.getExpenses(filters),
  });
}

export function useAdminExpense(id: number | null) {
  return useQuery({
    queryKey: ['admin', 'expenses', id],
    queryFn: () => adminService.getExpense(id!),
    enabled: !!id,
  });
}

export function useCreateExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BusinessExpenseCreate) => adminService.createExpense(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'expenses'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

export function useUpdateExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: BusinessExpenseUpdate }) =>
      adminService.updateExpense(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'expenses'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminService.deleteExpense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'expenses'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

// Impersonation
export function useImpersonateTenant() {
  return useMutation({
    mutationFn: (tenantId: number) => adminService.impersonateTenant(tenantId),
  });
}

export function useStopImpersonation() {
  return useMutation({
    mutationFn: () => adminService.stopImpersonation(),
  });
}

// Reset Password
export function useResetUserPassword() {
  return useMutation({
    mutationFn: (userId: number) => adminService.resetUserPassword(userId),
  });
}
